import React, { useState, useEffect, useCallback } from 'react';
import { Send } from 'lucide-react';
import Modal2, { Modal2Footer, Modal2Button } from '../../common/Modal2';
import TabSelector from '../../common/TabSelector';
import { ConfirmationModal, SaveAsTemplateModal } from '../modals';
import SurveyDetailsAccordion from './accordions/SurveyDetailsAccordion';
import AssignSurveyAccordion from './accordions/AssignSurveyAccordion';
import QuestionsAccordion from './accordions/QuestionsAccordion';
import ScheduleAccordion from './accordions/ScheduleAccordion';
import LoadTemplateMenu from './components/LoadTemplateMenu';
import { useSurveyBuilder } from '../hooks';
import { ValidationError } from '../types';
import { questionCountLabel } from '../../../utils/surveyCreation';

type CreateStep = 'details' | 'questions' | 'settings';

export type SurveyWizardMode = 'create' | 'view' | 'edit';
export type SurveyWizardEntity = 'survey' | 'draft' | 'template';

interface CreateSurveyModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode?: SurveyWizardMode;
  entityType?: SurveyWizardEntity;
  entityId?: string;
}

const stepTabs = [
  { k: 'details' as const, label: 'Details' },
  { k: 'questions' as const, label: 'Questions' },
  { k: 'settings' as const, label: 'Settings' },
];

/** Figma Create Survey wizard (dc.html:4432): 3 steps + template menu + save footer. */
const CreateSurveyModal: React.FC<CreateSurveyModalProps> = ({
  isOpen,
  onClose,
  mode = 'create',
  entityType = 'survey',
  entityId,
}) => {
  const [step, setStep] = useState<CreateStep>('details');
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);

  const {
    formState,
    isLoading,
    isSaving,
    isPublishing,
    setTitle,
    setDescription,
    setSchedule,
    setAssignment,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    reorderQuestions,
    loadTemplate,
    loadDraft,
    clearForm,
    saveAsDraft,
    saveAsNewTemplate,
    updateExistingTemplate,
    publishSurvey,
    validationErrors,
  } = useSurveyBuilder();

  useEffect(() => {
    if (!isOpen) return;
    setStep('details');
    if (mode === 'create' || !entityId) {
      clearForm();
    } else if (entityType === 'template') {
      loadTemplate(entityId);
    } else {
      loadDraft(entityId);
    }
  }, [isOpen, mode, entityType, entityId, clearForm, loadTemplate, loadDraft]);

  const getFieldError = useCallback(
    (field: string) => validationErrors.find((e: ValidationError) => e.field === field)?.message,
    [validationErrors]
  );

  const handleLoadTemplate = useCallback(
    async (templateId: string) => {
      await loadTemplate(templateId);
    },
    [loadTemplate]
  );

  const handlePublish = useCallback(async () => {
    const success = await publishSurvey();
    if (success) {
      clearForm();
      onClose();
    }
  }, [publishSurvey, clearForm, onClose]);

  const handleSaveAsTemplate = () => {
    setShowSaveTemplateModal(true);
  };

  const questionCount = formState.questions.length;
  const readOnly = mode === 'view';
  const entityLoading = mode !== 'create' && isLoading;

  const modalTitle =
    mode === 'view'
      ? entityType === 'template'
        ? 'View Template'
        : 'View Survey'
      : mode === 'edit'
        ? entityType === 'template'
          ? 'Edit Template'
          : 'Edit Draft'
        : 'Create Survey';

  return (
    <>
      <Modal2
        isOpen={isOpen}
        onClose={onClose}
        title={modalTitle}
        subtitle={readOnly ? undefined : `New survey · ${questionCountLabel(questionCount)}`}
        width={620}
        footer={
          readOnly ? (
            <Modal2Footer justify="end">
              <Modal2Button variant="secondary" onClick={onClose}>
                Close
              </Modal2Button>
            </Modal2Footer>
          ) : (
          <Modal2Footer justify="end">
            {formState.sourceTemplateId && (
              <Modal2Button
                variant="primary"
                onClick={() => updateExistingTemplate()}
                loading={isSaving}
                disabled={isSaving || isPublishing}
              >
                Update Template
              </Modal2Button>
            )}
            <Modal2Button
              variant="secondary"
              onClick={handleSaveAsTemplate}
              disabled={isSaving || isPublishing}
            >
              Save as Template
            </Modal2Button>
            <Modal2Button
              variant="secondary"
              onClick={() => saveAsDraft()}
              loading={isSaving}
              disabled={isSaving || isPublishing}
            >
              Save as Draft
            </Modal2Button>
            <Modal2Button
              variant="primary"
              onClick={handlePublish}
              loading={isPublishing}
              disabled={isSaving || isPublishing}
            >
              <Send className="w-4 h-4" />
              Publish
            </Modal2Button>
            </Modal2Footer>
          )
        }
      >
        {entityLoading ? (
          <div className="py-10 space-y-4" aria-busy="true">
            <div className="h-5 w-2/3 bg-[var(--color-bg-skeleton)] rounded animate-pulse" />
            <div className="h-4 w-full bg-[var(--color-bg-skeleton)] rounded animate-pulse" />
            <div className="h-24 w-full bg-[var(--color-bg-skeleton)] rounded-[12px] animate-pulse" />
            <div className="h-4 w-1/2 bg-[var(--color-bg-skeleton)] rounded animate-pulse" />
          </div>
        ) : (
          <>
        <div className="flex items-center justify-between gap-3 mb-5">
          <TabSelector tabs={stepTabs} active={step} onChange={setStep} size="sm" />
          {!readOnly && <LoadTemplateMenu onSelect={handleLoadTemplate} align="right" />}
        </div>

        {step === 'details' && (
          <div className="space-y-5">
            <SurveyDetailsAccordion
              bare
              title={formState.title}
              description={formState.description}
              onTitleChange={setTitle}
              onDescriptionChange={setDescription}
              titleError={getFieldError('title')}
              readOnly={readOnly}
            />
            <AssignSurveyAccordion
              bare
              assignment={formState.assignment}
              onAssignmentChange={setAssignment}
              entityTypeError={getFieldError('assignment.entityType')}
              entityIdsError={getFieldError('assignment.entityIds')}
              readOnly={readOnly}
            />
          </div>
        )}

        {step === 'questions' && (
          <QuestionsAccordion
            bare
            questions={formState.questions}
            onAddQuestion={addQuestion}
            onUpdateQuestion={updateQuestion}
            onDeleteQuestion={deleteQuestion}
            onReorderQuestions={reorderQuestions}
            questionsError={getFieldError('questions')}
            readOnly={readOnly}
          />
        )}

        {step === 'settings' && (
          <ScheduleAccordion
            bare
            schedule={formState.schedule}
            onScheduleChange={setSchedule}
            scheduleError={getFieldError('schedule')}
            readOnly={readOnly}
          />
        )}
          </>
        )}

      </Modal2>

      <SaveAsTemplateModal
        isOpen={showSaveTemplateModal}
        onClose={() => setShowSaveTemplateModal(false)}
        onSave={saveAsNewTemplate}
        defaultName={formState.title}
        isSaving={isSaving}
      />
    </>
  );
};

export default CreateSurveyModal;
